import { createLineSwap, paintFailure } from './cart-line-swap.js';
import { originOf, withOrigin } from './addon-origin.js';

const MAX_CHARS = 22;
const GOLD_FOIL_ID = 'gold';
const FOCUS_MS = 320;
const FRAME_ENGRAVING_KEYS = ['Cor da gravação', 'Palavra', 'Orientação'];

function goldFirstForTheBag(foils) {
  const gold = foils.find((foil) => foil.id === GOLD_FOIL_ID);
  return gold ? [gold, ...foils.filter((foil) => foil !== gold)] : foils.slice();
}

export function createCartEngrave({ root, cart, sheet, host, importModule, fetch, delay, track, notify, pathname, isTouch }) {
  const drawer = root.getElementById('cart-drawer');
  if (!drawer || !cart) return null;

  const addUrl = drawer.dataset.cartAddUrl;
  const changeUrl = drawer.dataset.cartChangeUrl;
  const cartJsonUrl = (drawer.dataset.cartUrl || '/cart') + '.js';
  const copy = { added: drawer.dataset.noticeAdded || '', removed: drawer.dataset.noticeRemoved || '' };
  const errorNode = drawer.querySelector('[data-cart-error]');
  const genericError = errorNode ? (errorNode.dataset.errorGeneric || '') : '';
  const sheetNode = root.querySelector('[data-cart-sheet]');
  const sheetCopy = {
    remove: (sheetNode && sheetNode.dataset.sheetRemove) || '',
    later: (sheetNode && sheetNode.dataset.sheetLater) || '',
    ghost: (sheetNode && sheetNode.dataset.engraveGhost) || '',
  };

  let surfaceModules = {};
  try { surfaceModules = JSON.parse(drawer.dataset.editModules || '{}'); } catch { surfaceModules = {}; }
  let framePromise = null;
  const frameModule = () => {
    if (!framePromise) framePromise = importModule(surfaceModules['porta-retrato']).catch((error) => { framePromise = null; throw error; });
    return framePromise;
  };

  const el = (tag, attributes, ...children) => {
    const node = root.createElement(tag);
    Object.keys(attributes || {}).forEach((name) => {
      const value = attributes[name];
      if (value === false || value === null || value === undefined) return;
      if (name === 'text') node.textContent = value;
      else if (name === 'hidden') node.hidden = true;
      else if (name === 'disabled') node.disabled = true;
      else node.setAttribute(name, value === true ? '' : String(value));
    });
    node.append(...children.filter(Boolean));
    return node;
  };

  async function loadConfig(productUrl) {
    const productDocument = await host.loadProductDocument(productUrl);
    const island = productDocument.getElementById('pr-config');
    if (!island) throw new Error('cart-engrave: product page carries no #pr-config');
    const config = JSON.parse(island.textContent);
    if (!config.engrave || !config.engrave.variantId) throw new Error('cart-engrave: no engraving variant');
    return config;
  }

  async function post(url, payload) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...payload, sections: 'cart-drawer', sections_url: pathname }),
    });
    if (!response.ok) throw new Error('cart-engrave: write failed');
    return response.json();
  }

  async function readCart() {
    const response = await fetch(cartJsonUrl, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('cart-engrave: cart read failed');
    return response.json();
  }

  const swapOut = createLineSwap({ post, changeUrl, readCart });

  const render = (data) => {
    if (!cart.replaceContent(data && data.sections && data.sections['cart-drawer'])) cart.reload();
  };

  const accordionOf = (group) => Array.from(drawer.querySelectorAll('[data-upsell]')).find((node) => node.dataset.group === group) || null;
  const findById = (list, id) => (list || []).find((option) => option.id === id) || null;
  const withWidth = (src, width) => src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;

  function buildPopup(trigger, config, frame) {
    const data = trigger.dataset;
    const labels = config.labels || {};
    const group = data.group;
    const family = (trigger.closest('[data-upsell]') || { dataset: {} }).dataset.family || '';
    const added = !!data.lineKey;
    const foils = goldFirstForTheBag(config.foils || []);
    const orientations = config.orientations || [];
    const state = {
      word: added ? (data.engraveWord || '') : '',
      foilId: added ? frame.idForLabel(foils, data.engraveFoil) : (foils[0] ? foils[0].id : ''),
      orientId: added ? frame.idForLabel(orientations, data.engraveOrient) : (orientations[0] ? orientations[0].id : ''),
    };
    const colorLabel = data.frameColor || '';
    const renders = (config.framePreviews && config.framePreviews[colorLabel]) || null;

    const previewImg = el('img', { class: 'pr-preview__img', alt: '', width: '1915', height: '1634', draggable: 'false' });
    const previewLine = el('p', { class: 'pr-preview__line', 'data-engrave-preview-word': true });
    const preview = el('div', { class: 'pr-preview cart-engrave__preview', 'data-engrave-preview': true }, previewImg, previewLine);
    const previewWrap = el('div', { class: 'cart-engrave__stage' }, preview);
    const input = el('input', {
      class: 'cart-engrave__input', type: 'text', maxlength: String(MAX_CHARS), autocomplete: 'off', autocapitalize: 'characters', spellcheck: 'false',
      placeholder: labels.wordPlaceholder || '', 'aria-label': labels.word || '', 'data-engrave-input': true,
    });
    const foilReadout = el('span', { class: 'cart-engrave__readout', 'data-engrave-foil-readout': true });
    const foilButtons = foils.map((foil) => el('button', {
      type: 'button', class: 'cart-engrave__swatch', role: 'radio', 'aria-checked': 'false', 'aria-label': foil.label || foil.id, title: foil.label || '',
      'data-engrave-foil': foil.id, style: '--swatch: ' + (foil.hex || '#ccc'),
    }));
    const orientButtons = orientations.map((orient) => el('button', { type: 'button', role: 'radio', class: 'cart-photos__tab', 'data-engrave-orient': orient.id, text: orient.label || orient.id }));
    const cta = el('button', { type: 'button', class: 'cart-sheet__cta', 'data-engrave-cta': true, disabled: true, text: added ? (data.ctaReplace || '') : (data.ctaAdd || '') });
    const secondary = el('button', { type: 'button', class: 'cart-sheet__later', 'data-engrave-secondary': true, text: added ? sheetCopy.remove : sheetCopy.later });

    const popup = el('div', { class: 'cart-engrave' },
      data.sheetDesc ? el('p', { class: 'cart-engrave__hint', text: data.sheetDesc }) : null,
      previewWrap,
      el('p', { class: 'cart-engrave__label', text: labels.word || '' }),
      input,
      el('div', { class: 'cart-engrave__row-head' }, el('span', { class: 'cart-engrave__label', text: labels.foil || '' }), foilReadout),
      el('div', { class: 'cart-engrave__swatches', role: 'radiogroup', 'aria-label': labels.foil || '' }, ...foilButtons),
      el('p', { class: 'cart-engrave__label', text: labels.orient || '' }),
      el('div', { class: 'cart-photos__tabs', role: 'radiogroup', 'aria-label': labels.orient || '' }, ...orientButtons),
      cta, secondary);

    const orientKey = () => (state.orientId === 'deitado' ? 'h' : 'v');

    function paint() {
      const word = state.word.trim();
      const key = orientKey();
      const src = renders && renders[key];
      if (src) previewImg.setAttribute('src', withWidth(src, 1400));
      else previewImg.removeAttribute('src');
      previewWrap.hidden = !src;
      const position = frame.PREVIEW_TEXT_POS[key];
      preview.style.setProperty('--text-x', position.x + '%');
      preview.style.setProperty('--text-y', position.y + '%');
      preview.style.setProperty('--text-size', position.size + 'cqw');
      preview.classList.toggle('pr-preview--gold', state.foilId === GOLD_FOIL_ID);
      preview.classList.toggle('pr-preview--silver', state.foilId !== GOLD_FOIL_ID);
      previewLine.textContent = word || sheetCopy.ghost;
      previewLine.classList.toggle('is-ghost', !word);
      previewLine.hidden = !(word || sheetCopy.ghost);
      foilButtons.forEach((button) => {
        const on = button.dataset.engraveFoil === state.foilId;
        button.classList.toggle('is-active', on);
        button.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      const foil = findById(foils, state.foilId);
      foilReadout.textContent = foil ? (foil.label || '') : '';
      orientButtons.forEach((button) => {
        const on = button.dataset.engraveOrient === state.orientId;
        button.classList.toggle('is-active', on);
        button.setAttribute('aria-checked', on ? 'true' : 'false');
        button.setAttribute('tabindex', on ? '0' : '-1');
      });
      cta.disabled = !word;
    }

    input.value = state.word;
    input.addEventListener('input', () => {
      state.word = String(input.value || '').slice(0, MAX_CHARS);
      if (input.value !== state.word) input.value = state.word;
      paint();
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (typeof input.blur === 'function') input.blur();
      }
    });
    foilButtons.forEach((button) => button.addEventListener('click', () => { state.foilId = button.dataset.engraveFoil; paint(); }));
    orientButtons.forEach((button, index) => {
      button.addEventListener('click', () => { state.orientId = button.dataset.engraveOrient; paint(); });
      button.addEventListener('keydown', (event) => {
        const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
        if (!step) return;
        event.preventDefault();
        const next = orientButtons[(index + step + orientButtons.length) % orientButtons.length];
        state.orientId = next.dataset.engraveOrient;
        paint();
        if (typeof next.focus === 'function') next.focus();
      });
    });
    cta.addEventListener('click', () => {
      const word = state.word.trim();
      if (!word) return;
      const foil = findById(foils, state.foilId);
      const orient = findById(orientations, state.orientId);
      const chosen = { word, foilLabel: (foil && foil.label) || '', orientLabel: (orient && orient.label) || '', orientId: state.orientId };
      sheet.close();
      commit(trigger, config, frame, chosen, { group, family });
    });
    secondary.addEventListener('click', () => {
      sheet.close();
      if (added) remove(trigger, config, frame, { group, family });
    });

    paint();
    return { popup, input };
  }

  function groupLines(cartData, group) {
    const items = (cartData && cartData.items) || [];
    const inGroup = items.filter((item) => (item.properties || {})._group === group);
    const engravings = inGroup.filter((item) => (item.properties || {})._addon === 'gravacao');
    return {
      main: inGroup.find((item) => !(item.properties || {})._addon) || null,
      engraving: engravings[0] || null,
      engravings,
    };
  }

  const engravingOf = (line, config, frame) => {
    const props = (line && line.properties) || {};
    const orientLabel = props['Orientação'] || '';
    return { word: props['Gravação'] || '', foilLabel: props['Cor do texto'] || '', orientLabel, orientId: config && frame ? frame.idForLabel(config.orientations || [], orientLabel) : '' };
  };

  function framePropsFor(config, frame, main, engraving) {
    const current = main.properties || {};
    const props = Object.fromEntries(Object.entries(current).filter(([name]) => !FRAME_ENGRAVING_KEYS.includes(name)));
    const cover = config && frame
      ? frame.coverImageFor(config, { colorLabel: current['Cor da moldura'] || '', engraveOn: !!engraving, orientId: engraving ? engraving.orientId : '' })
      : '';
    if (cover) props._cover_image = cover;
    if (engraving) {
      if (engraving.foilLabel) props['Cor da gravação'] = engraving.foilLabel;
      if (engraving.word) props['Palavra'] = engraving.word;
      if (engraving.orientLabel) props['Orientação'] = engraving.orientLabel;
    }
    return props;
  }

  const sameProps = (left, right) => Array.from(new Set([...Object.keys(left), ...Object.keys(right)]))
    .every((name) => (left[name] || '') === (right[name] || ''));

  async function alignFrame(config, frame, main, engraving) {
    if (!main) return null;
    const props = framePropsFor(config, frame, main, engraving);
    if (sameProps(props, main.properties || {})) return null;
    return post(changeUrl, { id: main.key, quantity: main.quantity, properties: props });
  }

  const withFrameProps = (config, frame, main, engraving) => ({ ...main, properties: framePropsFor(config, frame, main, engraving) });

  function sameAsLine(engraving, properties) {
    if (!engraving) return false;
    const current = engraving.properties || {};
    return ['Gravação', 'Cor do texto', 'Orientação'].every((name) => (current[name] || '') === (properties[name] || ''));
  }

  function commit(trigger, config, frame, chosen, { group, family }) {
    const data = trigger.dataset;
    cart.hideError();
    return cart.enqueueWrite(async () => {
      if (!accordionOf(group)) return;
      let painted = null;
      try {
        const { main, engraving } = groupLines(await readCart(), group);
        if (!main) return;
        const properties = withOrigin(
          { _group: group, _addon: 'gravacao', 'Gravação': chosen.word },
          engraving ? originOf(engraving) : 'cart',
        );
        if (chosen.foilLabel) properties['Cor do texto'] = chosen.foilLabel;
        if (chosen.orientLabel) properties['Orientação'] = chosen.orientLabel;
        if (sameAsLine(engraving, properties)) {
          painted = await alignFrame(config, frame, main, chosen);
          if (painted) render(painted);
          return;
        }
        painted = await alignFrame(config, frame, main, chosen);
        const frameMoved = !!painted;
        try {
          let added = null;
          try {
            added = await post(addUrl, { items: [{ id: Number(config.engrave.variantId), quantity: 1, properties }] });
            painted = added;
          } catch (error) {
            const held = groupLines(await readCart(), group).engravings.find((line) => sameAsLine(line, properties));
            if (!held) throw error;
            added = { items: [{ key: held.key }] };
            painted = null;
          }
          if (engraving) {
            try {
              painted = await swapOut(engraving.key, added);
            } catch (error) {
              if (!error.resolved) throw error;
              painted = null;
            }
          }
        } catch (error) {
          if (frameMoved) {
            try {
              const now = groupLines(await readCart(), group);
              const held = now.engravings.some((line) => sameAsLine(line, properties));
              const oldGone = !engraving || !now.engravings.some((line) => line.key === engraving.key);
              if (held && oldGone) {
                error.resolved = true;
              } else if (held) {
                error.stale = true;
              } else if (now.main) {
                const kept = now.engraving ? engravingOf(now.engraving, config, frame) : null;
                error.painted = await post(changeUrl, { id: now.main.key, quantity: now.main.quantity, properties: framePropsFor(config, frame, now.main, kept) });
              } else {
                error.stale = true;
              }
            } catch {
              error.painted = null;
              error.stale = true;
            }
          }
          throw error;
        }
        track('cart_upsell_add', { addon: 'gravacao', value_cents: Number(config.engrave.price) || 0, origin: 'cart', family });
        if (painted) render(painted);
        else cart.reload();
        if (copy.added) notify({ title: copy.added, subtitle: (data.noticeSubtitle || '').replace('{word}', chosen.word), group });
      } catch (error) {
        paintFailure({ cart, render }, error, painted);
      }
    });
  }

  function remove(trigger, config, frame, { group, family }) {
    return removeEngraving(config, frame, group, trigger.dataset.noticeName || '', () => track('cart_upsell_remove', { addon: 'gravacao', family }));
  }

  function removeEngraving(config, frame, group, noticeName, onRemoved, key = null) {
    cart.hideError();
    return cart.enqueueWrite(async () => {
      let painted = null;
      try {
        const { main, engraving: first, engravings } = groupLines(await readCart(), group);
        const engraving = engravings.find((line) => line.key === key) || first;
        if (!engraving) return;
        const kept = engravingOf(engraving, config, frame);
        painted = await alignFrame(config, frame, main, null);
        const standing = main && painted ? withFrameProps(config, frame, main, null) : main;
        try {
          painted = await post(changeUrl, { id: engraving.key, quantity: 0 });
        } catch (error) {
          try {
            const stillEngraved = ((await readCart()).items || []).some((item) => item.key === engraving.key);
            if (stillEngraved) error.painted = (await alignFrame(config, frame, standing, kept)) || painted;
            else error.resolved = true;
          } catch {
            error.painted = null;
            error.stale = true;
          }
          throw error;
        }
        onRemoved();
        render(painted);
        if (copy.removed) notify({ title: copy.removed, subtitle: noticeName, group });
      } catch (error) {
        paintFailure({ cart, render }, error, painted);
      }
    });
  }

  async function openPopup(trigger) {
    const token = sheet.stamp();
    const family = (trigger.closest('[data-upsell]') || { dataset: {} }).dataset.family || '';
    trigger.setAttribute('aria-busy', 'true');
    let config = null;
    let frame = null;
    try {
      [config, frame] = await Promise.all([loadConfig(trigger.dataset.productUrl), frameModule()]);
    } catch {
      config = null;
    } finally {
      trigger.removeAttribute('aria-busy');
    }
    if (!sheet.isCurrent(token)) return;
    let content;
    let focusLater = null;
    if (config && frame) {
      const built = buildPopup(trigger, config, frame);
      content = built.popup;
      focusLater = built.input;
    } else {
      content = el('div', { class: 'cart-engrave' },
        el('p', { class: 'cart-engrave__status', role: 'status', text: genericError }),
        el('button', { type: 'button', class: 'cart-sheet__later', 'data-engrave-secondary': true, text: sheetCopy.later }));
      content.querySelector('[data-engrave-secondary]').addEventListener('click', () => sheet.close());
    }
    let cancelFocus = null;
    sheet.openCustom({
      title: trigger.dataset.noticeName || '',
      tag: trigger.dataset.priceFormatted || '',
      opener: trigger,
      sheet: 'gravacao',
      family,
      group: trigger.dataset.group,
      mountInto: (node) => node.append(content),
      onClose: () => { if (cancelFocus) cancelFocus(); },
    });
    if (focusLater && !focusLater.value && !isTouch()) {
      cancelFocus = delay(() => { if (typeof focusLater.focus === 'function') focusLater.focus({ preventScroll: true }); }, FOCUS_MS);
    }
  }

  async function removeFromChip(chip) {
    chip.setAttribute('aria-busy', 'true');
    const name = chip.querySelector('.cart-addon-chip__name');
    const noticeName = name ? name.textContent.trim() : '';
    let config = null;
    let frame = null;
    try {
      [config, frame] = await Promise.all([loadConfig(chip.dataset.engraveProductUrl), frameModule()]);
    } catch {
      config = null;
    }
    const loaded = !!(config && frame);
    return removeEngraving(loaded ? config : null, loaded ? frame : null, chip.dataset.group, noticeName, () => {}, chip.dataset.key);
  }

  drawer.addEventListener('click', (event) => {
    const button = event.target.closest('[data-action="remove"]');
    const chip = button && button.closest('[data-engrave-product-url]');
    if (!chip || !drawer.contains(chip) || !chip.dataset.group || !chip.dataset.key) return;
    event.preventDefault();
    event.stopPropagation();
    removeFromChip(chip);
  }, true);

  drawer.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-action="upsell-engrave"]');
    if (!trigger || !drawer.contains(trigger)) return;
    event.preventDefault();
    openPopup(trigger);
  });

  return { openPopup };
}

if (typeof document !== 'undefined') {
  const start = () => {
    createCartEngrave({
      root: document,
      cart: window.fnCart,
      sheet: {
        openCustom: (options) => window.fnCartSheet && window.fnCartSheet.openCustom(options),
        close: () => window.fnCartSheet && window.fnCartSheet.close(),
        stamp: () => (window.fnCartSheet ? window.fnCartSheet.stamp() : 0),
        isCurrent: (token) => (window.fnCartSheet ? window.fnCartSheet.isCurrent(token) : true),
      },
      host: {
        loadProductDocument: (url) => (window.fnCartEdit
          ? window.fnCartEdit.loadProductDocument(url)
          : Promise.reject(new Error('cart-engrave: no edit host'))),
      },
      importModule: (url) => import(url),
      fetch: (url, init) => window.fetch(url, init),
      delay: (callback, ms) => {
        const id = setTimeout(callback, ms);
        return () => clearTimeout(id);
      },
      track: (event, payload) => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event, ...payload });
      },
      notify: (payload) => {
        if (window.fnCartNotice) window.fnCartNotice.show(payload);
      },
      pathname: window.location.pathname,
      isTouch: () => navigator.maxTouchPoints > 0,
    });
  };
  if (window.fnCart) start();
  else document.addEventListener('fn:cart-ready', start, { once: true });
}
